// CMM (Coordinate Measuring Machine) Inspection — multi-point GD&T report.
// Features: GD&T per dimension (cylindricity, parallelism, perpendicularity, etc.)
// + per-feature pass/fail + overall part report + trend mini-chart.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Typography, Button, TextField, MenuItem, Stack, Chip, IconButton, LinearProgress,
} from '@mui/material';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { customerbasedshift, shiftadd } from '../../Services/app/masterservice';
import { cleanCustomerId } from '../../Services/app/operatorservice';

const STORAGE_KEY = 'quality_inspections_cmm';
const GDT_SYMBOLS = ['Linear ø', 'Cylindricity ⌭', 'Flatness ▱', 'Parallelism ∥', 'Perpendicularity ⊥', 'Position ⊕', 'Concentricity ◎', 'Runout ↗'];

const CmmScreen = () => {
  const cid = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [components, setComponents] = useState([]);
  const [history, setHistory] = useState([]);

  const [partCode, setPartCode] = useState('');
  const [serial, setSerial] = useState('');
  const [cmmId, setCmmId] = useState('CMM-01 (Zeiss Contura)');
  const [operator, setOperator] = useState(localStorage.getItem('firstName')?.replace(/"/g, '') || 'CMM Tech');
  const [program, setProgram] = useState('PRG-AUTO-DECKEL.cmm');
  const [features, setFeatures] = useState([
    { name: 'Bore Ø32 H7', gdt: 'Linear ø', nominal: 32.000, upperTol: 0.025, lowerTol: 0,    actual: 32.012, x: -42.10, y: 0.00, z: -8.50 },
    { name: 'Bore Ø32 H7', gdt: 'Cylindricity ⌭', nominal: 0,      upperTol: 0.015, lowerTol: 0,    actual: 0.008,  x: -42.10, y: 0.00, z: -8.50 },
    { name: 'Face A',       gdt: 'Flatness ▱',     nominal: 0,     upperTol: 0.020, lowerTol: 0,    actual: 0.012,  x: 0,      y: 0,    z: 0 },
    { name: 'Face B ∥ A',   gdt: 'Parallelism ∥',  nominal: 0,     upperTol: 0.030, lowerTol: 0,    actual: 0.045,  x: 0,      y: 0,    z: 18.0 },
    { name: 'Pin Hole ⊥ A', gdt: 'Perpendicularity ⊥', nominal: 0, upperTol: 0.025, lowerTol: 0,    actual: 0.018,  x: 32.00,  y: 0,    z: 0 },
    { name: 'Hole pattern PCD 64', gdt: 'Position ⊕', nominal: 64.000, upperTol: 0.05, lowerTol: 0.05, actual: 64.012, x: 0, y: 0, z: 0 },
  ]);

  useEffect(() => {
    (async () => {
      try { const r = await customerbasedshift(cid, 'component'); setComponents(Array.isArray(r?.[0]?.value) ? r[0].value : []); } catch { setComponents([]); }
      try { const r = await customerbasedshift(cid, STORAGE_KEY); setHistory(Array.isArray(r?.[0]?.value) ? r[0].value : []); } catch { setHistory([]); }
    })();
  }, [cid]);

  const evalFeat = (f) => {
    if (f.actual === '' || Number.isNaN(Number(f.actual))) return null;
    const v = Number(f.actual);
    const lo = Number(f.nominal) - Number(f.lowerTol);
    const hi = Number(f.nominal) + Number(f.upperTol);
    return v >= lo && v <= hi ? 'PASS' : 'FAIL';
  };
  const deviation = (f) => {
    if (f.actual === '' || Number.isNaN(Number(f.actual))) return null;
    return Number(f.actual) - Number(f.nominal);
  };
  const totalPass = features.filter(f => evalFeat(f) === 'PASS').length;
  const totalFail = features.filter(f => evalFeat(f) === 'FAIL').length;
  const overall = totalFail > 0 ? 'FAIL' : (totalPass === features.length ? 'PASS' : 'PARTIAL');
  const passPct = features.length ? Math.round((totalPass / features.length) * 100) : 0;

  const partOptions = components.slice(0, 30).map(c => ({ code: c.component_number || '', name: c.component_name || '' }));

  const save = async () => {
    if (!partCode) return;
    const entry = {
      id: 'QC-CMM-' + Date.now(), ts: Date.now(),
      partCode, serial, cmmId, program, operator, result: overall,
      pass: totalPass, fail: totalFail, total: features.length, passPct,
      features: features.map(f => ({ ...f, actual: Number(f.actual), deviation: deviation(f), result: evalFeat(f) })),
    };
    const next = [entry, ...history].slice(0, 200);
    setHistory(next);
    try { await shiftadd({ [STORAGE_KEY]: next }, cid, 'SERVER_SCOPE'); } catch (e) { console.error(e); }
    setSerial('');
  };

  const tone = overall === 'PASS' ? '#16a34a' : overall === 'FAIL' ? '#dc2626' : '#f59e0b';
  const toneBg = overall === 'PASS' ? '#dcfce7' : overall === 'FAIL' ? '#fee2e2' : '#fef3c7';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <PrecisionManufacturingIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>CMM Inspection · GD&T Report</Typography>
        <Chip label={`${history.length} parts inspected`} size="small" sx={{ ml: 'auto' }} />
      </Stack>

      {/* Header card */}
      <Card sx={{ p: 2.5, mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField select label="Component" value={partCode} onChange={(e) => setPartCode(e.target.value)} size="small" sx={{ flex: 2 }}>
            {partOptions.map(p => (
              <MenuItem key={p.code} value={p.code}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, marginRight: 8 }}>{p.code}</span>
                <span style={{ color: '#6b7280', fontSize: 12 }}>{p.name}</span>
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Serial" value={serial} onChange={(e) => setSerial(e.target.value)} size="small" sx={{ flex: 1 }} />
          <TextField label="CMM" value={cmmId} onChange={(e) => setCmmId(e.target.value)} size="small" sx={{ flex: 1.2 }} />
          <TextField label="Program" value={program} onChange={(e) => setProgram(e.target.value)} size="small" sx={{ flex: 1.5 }} />
          <TextField label="Operator" value={operator} onChange={(e) => setOperator(e.target.value)} size="small" sx={{ flex: 1 }} />
        </Stack>
      </Card>

      {/* Summary band */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Card sx={{ p: 2.5, flex: 1, borderRadius: 3, border: `3px solid ${tone}`, bgcolor: toneBg }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: tone, letterSpacing: 1.2, mb: 0.5 }}>VERDICT</Typography>
          <Typography sx={{ fontSize: 46, fontWeight: 900, color: tone, lineHeight: 1.1 }}>{overall}</Typography>
          <Typography sx={{ fontSize: 13, color: tone, opacity: 0.9, fontWeight: 700 }}>
            {totalPass} of {features.length} features within tolerance
          </Typography>
          <LinearProgress variant="determinate" value={passPct} sx={{
            mt: 1.5, height: 10, borderRadius: 5, bgcolor: 'background.paper',
            '& .MuiLinearProgress-bar': { bgcolor: tone, borderRadius: 5 },
          }} />
        </Card>
        <Card sx={{ p: 2.5, width: { xs: '100%', md: 200 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: 1.2 }}>PASS / FAIL</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 32, fontWeight: 900, color: '#16a34a' }}>{totalPass}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700 }}>PASS</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 32, fontWeight: 900, color: '#dc2626' }}>{totalFail}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700 }}>FAIL</Typography>
            </Box>
          </Stack>
        </Card>
        <Stack spacing={1.5} sx={{ minWidth: 200 }}>
          <Button variant="outlined" startIcon={<UploadFileIcon />} sx={{ textTransform: 'none', justifyContent: 'flex-start' }}>
            Import .cmm probe data
          </Button>
          <Button variant="contained" color="primary" onClick={save} disabled={!partCode} sx={{ py: 1.5, textTransform: 'none', fontWeight: 800 }}>
            Save part report
          </Button>
        </Stack>
      </Stack>

      {/* Features table */}
      <Card sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1.3fr 0.9fr 0.7fr 0.7fr 1fr 1fr 0.7fr 32px', gap: 1, py: 0.5, alignItems: 'center' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>FEATURE</Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>GD&T</Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>NOMINAL</Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>+ TOL</Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>− TOL</Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>MEASURED</Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>DEV (mm)</Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>STATUS</Typography>
          <Box />
        </Box>
        {features.map((f, idx) => {
          const ev = evalFeat(f);
          const dev = deviation(f);
          const rowTone = ev === 'PASS' ? '#16a34a' : ev === 'FAIL' ? '#dc2626' : '#9ca3af';
          return (
            <Box key={idx} sx={{
              display: 'grid', gridTemplateColumns: '1.5fr 1.3fr 0.9fr 0.7fr 0.7fr 1fr 1fr 0.7fr 32px',
              gap: 1, py: 0.75, alignItems: 'center', borderTop: '1px solid', borderColor: 'divider',
            }}>
              <TextField size="small" value={f.name} onChange={(e) => { const n = [...features]; n[idx].name = e.target.value; setFeatures(n); }} />
              <TextField select size="small" value={f.gdt} onChange={(e) => { const n = [...features]; n[idx].gdt = e.target.value; setFeatures(n); }}>
                {GDT_SYMBOLS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </TextField>
              <TextField size="small" type="number" value={f.nominal} onChange={(e) => { const n = [...features]; n[idx].nominal = e.target.value; setFeatures(n); }} inputProps={{ step: 0.001 }} />
              <TextField size="small" type="number" value={f.upperTol} onChange={(e) => { const n = [...features]; n[idx].upperTol = e.target.value; setFeatures(n); }} inputProps={{ step: 0.001 }} />
              <TextField size="small" type="number" value={f.lowerTol} onChange={(e) => { const n = [...features]; n[idx].lowerTol = e.target.value; setFeatures(n); }} inputProps={{ step: 0.001 }} />
              <TextField size="small" type="number" value={f.actual} onChange={(e) => { const n = [...features]; n[idx].actual = e.target.value; setFeatures(n); }} inputProps={{ step: 0.001 }}
                sx={{ '& input': { fontFamily: 'monospace', fontWeight: 700, color: rowTone } }} />
              <Typography sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: rowTone }}>
                {dev !== null ? (dev >= 0 ? '+' : '') + dev.toFixed(3) : '—'}
              </Typography>
              <Chip label={ev || '—'} size="small" sx={{
                bgcolor: ev === 'PASS' ? '#dcfce7' : ev === 'FAIL' ? '#fee2e2' : 'action.hover',
                color: ev === 'PASS' ? '#14532d' : ev === 'FAIL' ? '#7a0000' : 'text.secondary',
                fontWeight: 800,
              }} />
              <IconButton size="small" onClick={() => setFeatures(features.filter((_, i) => i !== idx))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
        <Button startIcon={<AddIcon />} variant="outlined" size="small" sx={{ mt: 2, textTransform: 'none' }}
          onClick={() => setFeatures([...features, { name: '', gdt: GDT_SYMBOLS[0], nominal: 0, upperTol: 0.05, lowerTol: 0, actual: '', x: 0, y: 0, z: 0 }])}>
          Add feature
        </Button>
      </Card>

      {/* Recent */}
      <Card sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.secondary', mb: 1.5, letterSpacing: 1 }}>
          RECENT CMM REPORTS
        </Typography>
        {history.length === 0 && <Typography sx={{ color: 'text.disabled', fontSize: 13, py: 2 }}>No reports yet.</Typography>}
        {history.slice(0, 20).map(h => (
          <Box key={h.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Chip label={h.result} size="small" sx={{
              bgcolor: h.result === 'PASS' ? '#dcfce7' : h.result === 'FAIL' ? '#fee2e2' : '#fef3c7',
              color: h.result === 'PASS' ? '#14532d' : h.result === 'FAIL' ? '#7a0000' : '#92400e',
              fontWeight: 800, minWidth: 80,
            }} />
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>{h.partCode}</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {h.serial} · {h.cmmId} · {h.pass}/{h.total} features
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <LinearProgress variant="determinate" value={h.passPct} sx={{
              width: 80, height: 6, borderRadius: 3, bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': { bgcolor: h.passPct === 100 ? '#16a34a' : h.passPct >= 80 ? '#f59e0b' : '#dc2626' },
            }} />
            <Typography sx={{ fontSize: 11, color: 'text.disabled', minWidth: 100, textAlign: 'right' }}>
              {new Date(h.ts).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
            </Typography>
          </Box>
        ))}
      </Card>
    </Box>
  );
};

export default CmmScreen;
